import { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMediaPredicate } from 'react-media-hook';
import { useSearchParams } from 'react-router-dom';
import parse from 'html-react-parser';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { Button } from 'components/ui/button';
import { ScrollArea } from 'components/ui/scroll-area';
import ReportActions from '../ReportActions';
import { cn } from 'lib/utils';
import ScanVitalDetail from './ScanVitalDetail';
import { VoiceReadingData } from 'types/voice';
import { VoiceReportDetail } from 'services/voice-scan/type';

interface VitalSignDetailProps {
  readingId: string;
  data: Record<string, VoiceReadingData[]> | null;
  scanData: VoiceReadingData[];
  voiceReportDetail: VoiceReportDetail | undefined;
  refetchScanReports: () => void;
  setSelectedReadingId: (id: string) => void;
}

export default function VitalSignDetail({
  data,
  scanData,
  readingId,
  refetchScanReports,
  setSelectedReadingId,
  voiceReportDetail,
}: VitalSignDetailProps) {
  const { t } = useTranslation();
  const isMediaMobile = useMediaPredicate('(max-width: 640px)');

  const [searchParams, setSearchParams] = useSearchParams();
  const vitalKey = searchParams.get('vital') || '';

  const [selectedVital, setSelectedVital] = useState<VoiceReadingData>();

  const onVitalSelect = (vitalKey: string) => {
    if (selectedVital && selectedVital.key === vitalKey) {
      setSelectedVital(undefined);
      return;
    }

    const vital = scanData?.find((data) => data.key === vitalKey);
    setSelectedVital(vital);
  };

  useEffect(() => {
    if (vitalKey && scanData && scanData.length) {
      onVitalSelect(vitalKey);
      if (isMediaMobile) setSearchParams({});
    }
  }, [vitalKey, scanData, isMediaMobile]);

  if (selectedVital) {
    return (
      <div className='p-4 sm:hidden'>
        <div className='flex justify-end'>
          <Button
            variant='ghost'
            className='text-sm text-blue-500 uppercase hover:text-blue-500 h-fit'
            onClick={() => setSelectedVital(undefined)}
          >
            <ChevronLeft className='w-4 h-4 mr-2' /> {t('scan_report')}
          </Button>
        </div>

        <ScrollArea className='h-[calc(100vh-10rem)]'>
          <Accordion defaultValue='info' type='single' collapsible>
            <AccordionItem value='info'>
              <AccordionTrigger>{t('parameter_info')}</AccordionTrigger>
              <AccordionContent>
                <div className='space-y-4 font-medium'>
                  <p className='text-primary'>{selectedVital?.short_intro}</p>

                  <div
                    className='whitespace-pre-wrap'
                    style={{ wordBreak: 'break-word' }}
                  >
                    {selectedVital?.long_intro
                      ? selectedVital.long_intro
                          .split('\n')
                          ?.map((line, ind) => {
                            if (!line.trim()) {
                              return (
                                <div
                                  className='block w-1/2 h-4'
                                  key={`${line}-${ind}`}
                                />
                              );
                            }

                            return parse(line, {
                              replace(domNode: any) {
                                if (domNode?.data?.startsWith('https://')) {
                                  return (
                                    <a
                                      href={domNode.data}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='block text-blue-500'
                                    >
                                      {domNode.data}
                                    </a>
                                  );
                                }
                              },
                            });
                          })
                      : null}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            {selectedVital?.colorRange?.length ? (
              <AccordionItem value='ranges'>
                <AccordionTrigger>{t('parameter_ranges')}</AccordionTrigger>
                <AccordionContent>
                  {selectedVital?.colorRange?.map(
                    ({ color, category }, index) => {
                      return (
                        <div
                          key={index}
                          className={cn(
                            'flex items-center justify-between py-2 font-medium border-b',
                            index === 0 && 'border-t'
                          )}
                        >
                          <div className='flex items-center gap-x-2'>
                            <div
                              className='w-4 h-4 rounded-full'
                              style={{ backgroundColor: color }}
                            />
                            <p className='text-sm'>{category}</p>
                          </div>
                        </div>
                      );
                    }
                  )}
                </AccordionContent>
              </AccordionItem>
            ) : null}
          </Accordion>
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className='p-4 sm:hidden'>
      <div className='flex justify-end'>
        <Button
          variant='ghost'
          className='text-sm text-blue-500 uppercase hover:text-blue-500 h-fit'
          onClick={() => setSelectedReadingId('')}
        >
          <ChevronLeft className='w-4 h-4 mr-2' /> {t('previous_scans')}
        </Button>
      </div>

      <ReportActions
        scanData={voiceReportDetail}
        readingId={readingId}
        refetchScanReports={refetchScanReports}
      />

      <ScrollArea className='h-[calc(100vh-10rem)]'>
        {data
          ? Object.keys(data)?.map((category) => {
              return (
                <div key={category}>
                  <h2 className='text-xl font-semibold'>{category}</h2>

                  {data[category]?.length ? (
                    <div className='p-4 mt-2 ml-2 border-l-2 border-l-primary'>
                      {data[category]?.map((vital) => (
                        <ScanVitalDetail
                          vital={vital}
                          onSelect={onVitalSelect}
                          key={vital.key}
                          selectedVital={selectedVital}
                          category={category}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })
          : null}
      </ScrollArea>
    </div>
  );
}
